// Embeddable paid-ads lead form bundle entry (in-page rendering, no iframe).
// Registers:
//   <clinic-lead-widget site-id locale base-url source subject? clinic-id?
//                       submit-label? turnstile-site-key?>
//
// The third supported form, alongside <clinic-booking-widget> and
// <clinic-contact-widget>. It exists because a landing page bought its traffic:
// it asks for a name and a number, treats email as optional, and has no message
// box — see widget/src/lead-form.tsx. Everything downstream is the contact
// pipeline (Turnstile, consent ledger, Google Sheets mirror, practice
// notification, lead promotion), reached with `kind: 'callback'`.
//
// Ad attribution is read off the HOST page's URL here rather than asked of the
// host: the element shares the document, the parameters are already in it, and
// a landing page should not have to reimplement gclid capture to embed a form.
//
// Host events: `lead-start`, `lead-success` (detail incl. phone/email for
// enhanced conversions — stays in-page until the host decides), `lead-error`.

import { createRoot, type Root } from 'react-dom/client';
import { StrictMode, useEffect, useState } from 'react';
import { NextIntlClientProvider } from 'next-intl';
import '../../src/components/booking/booking-widget.css';
import { ensureMessages, messagesFor, normalizeLocale } from './messages';
import { dispatchHostEvent, fetchWithTimeout, injectStyles, readHostParams } from './shadow';
import { EmbedLoading, EmbedUnavailable } from './status';
import { LeadFormEmbed, type LeadSuccessDetail } from './lead-form';
import type { Locale } from '../../src/i18n/config';

/** Ad parameters worth carrying; anything else on the URL is the host's own. */
const ATTRIBUTION_KEYS = [
  'gclid',
  'gbraid',
  'wbraid',
  'fbclid',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
];

function readAttribution(): Record<string, string> {
  const out: Record<string, string> = {};
  try {
    const params = new URLSearchParams(window.location.search);
    for (const key of ATTRIBUTION_KEYS) {
      const value = params.get(key);
      // Bounded: this ends up in a jsonb column, and the URL is attacker-shaped.
      if (value) out[key] = value.slice(0, 200);
    }
    out.page = `${window.location.origin}${window.location.pathname}`;
  } catch {
    // A URL we cannot read is not a reason to withhold the form.
  }
  return out;
}

interface LeadAppProps {
  siteId: string;
  baseUrl: string;
  locale: Locale;
  source: string;
  subject?: string;
  submitLabel?: string;
  preselectClinic?: string;
  turnstileSiteKeyAttr?: string;
  attribution: Record<string, string>;
  onStart: () => void;
  onSuccess: (detail: LeadSuccessDetail) => void;
  onError: (code: string) => void;
}

interface PublicConfig {
  clinics: { id: string; slug: string | null; display_name: string }[];
  embed?: { theme?: string; turnstileSiteKey?: string | null };
}

function LeadEmbedApp(props: LeadAppProps) {
  const [cfg, setCfg] = useState<PublicConfig | null>(null);
  const [failed, setFailed] = useState(false);
  // Bumped by the failure panel's "try again" to re-run resolution.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    fetchWithTimeout(`${props.baseUrl}/api/public/clinics?site=${encodeURIComponent(props.siteId)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d: PublicConfig) => {
        if (!cancelled) setCfg(d);
      })
      .catch((err) => {
        console.warn('[clinic-lead-widget] config resolution failed:', err);
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
    // Props are stable per mount; attribute changes remount the root.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);

  if (failed) {
    return (
      <div className="booking-widget booking-widget--bare">
        <EmbedUnavailable
          locale={props.locale}
          kind="form"
          onRetry={() => {
            setFailed(false);
            setAttempt((a) => a + 1);
          }}
        />
      </div>
    );
  }
  if (!cfg) {
    return (
      <div className="booking-widget booking-widget--bare">
        <EmbedLoading locale={props.locale} />
      </div>
    );
  }

  return (
    <LeadFormEmbed
      baseUrl={props.baseUrl}
      locale={props.locale}
      clinics={cfg.clinics}
      preselectClinic={props.preselectClinic}
      turnstileSiteKey={props.turnstileSiteKeyAttr ?? cfg.embed?.turnstileSiteKey ?? undefined}
      theme={cfg.embed?.theme ?? 'halcyon'}
      source={props.source}
      subject={props.subject}
      submitLabel={props.submitLabel}
      attribution={props.attribution}
      onStart={props.onStart}
      onSuccess={props.onSuccess}
      onError={props.onError}
    />
  );
}

class ClinicLeadWidget extends HTMLElement {
  static observedAttributes = [
    'site-id',
    'clinic-id',
    'locale',
    'base-url',
    'source',
    'subject',
    'submit-label',
    'turnstile-site-key',
  ];

  private root: Root | null = null;
  private mounted = false;
  private mountGen = 0;

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  attributeChangedCallback(_name: string, oldValue: string | null, newValue: string | null) {
    if (!this.mounted) return;
    if (oldValue === newValue) return;
    this.unmount();
    this.connectedCallback();
  }

  disconnectedCallback() {
    this.unmount();
  }

  private unmount() {
    this.mountGen++;
    this.mounted = false;
    try {
      this.root?.unmount();
    } catch {
      // noop
    }
    this.root = null;
    this.shadowRoot?.replaceChildren();
  }

  connectedCallback() {
    if (this.mounted) return;
    this.mounted = true;
    const gen = ++this.mountGen;

    const siteId = this.getAttribute('site-id');
    const baseUrlRaw = this.getAttribute('base-url');
    if (!siteId || !baseUrlRaw) {
      this.renderError('configuration_missing');
      return;
    }
    const baseUrl = baseUrlRaw.replace(/\/$/, '');
    const locale = normalizeLocale(this.getAttribute('locale'));
    const attr = (n: string) => {
      const v = this.getAttribute(n);
      return v && v.trim() !== '' ? v.trim() : undefined;
    };
    const preselectClinic = attr('clinic-id') ?? readHostParams().clinic;

    const shadow = this.shadowRoot!;
    // Styles + locale catalog load in parallel; neither ever rejects (both
    // degrade — unstyled CSS, pt strings — rather than block the mount).
    void Promise.all([
      injectStyles(shadow, baseUrl, 'lead-widget.css'),
      ensureMessages(locale, baseUrl),
    ]).then(() => {
      if (gen !== this.mountGen) return;
      const container = document.createElement('div');
      shadow.appendChild(container);
      this.root = createRoot(container);
      this.root.render(
        <StrictMode>
          <NextIntlClientProvider
            locale={locale}
            timeZone="Europe/Lisbon"
            messages={messagesFor(locale) as never}
          >
            <LeadEmbedApp
              siteId={siteId}
              baseUrl={baseUrl}
              locale={locale}
              source={attr('source') ?? 'widget:lead'}
              subject={attr('subject')}
              submitLabel={attr('submit-label')}
              preselectClinic={preselectClinic}
              turnstileSiteKeyAttr={attr('turnstile-site-key')}
              attribution={readAttribution()}
              onStart={() => dispatchHostEvent(this, 'lead-start')}
              onSuccess={(detail) => dispatchHostEvent(this, 'lead-success', detail)}
              onError={(code) => dispatchHostEvent(this, 'lead-error', { code })}
            />
          </NextIntlClientProvider>
        </StrictMode>,
      );
    });
  }

  private renderError(code: string) {
    const div = document.createElement('div');
    div.style.cssText = 'padding:24px;text-align:center;color:#6b7280;font-family:system-ui;';
    div.textContent = `Form unavailable (${code}).`;
    this.shadowRoot?.appendChild(div);
  }
}

if (!customElements.get('clinic-lead-widget')) {
  customElements.define('clinic-lead-widget', ClinicLeadWidget);
}

export {};
